import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { Link, useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useUpdateClinicSettingsConversationsSmsMutation,
  useGetClinicSettingsConversationsSmsQuery,
  useGetClinicPhoneNumbersQuery,
  ServiceCapability,
  useUpdateOneClinicPhoneNumberMutation,
  useUpdateClinicSettingsConversationsTransferTwilioNumberMutation,
  ServiceProvider,
  PhoneNumberStatus,
  useSubscribeToClinicPhoneNumbersSubscription,
  useFindClinicLegalEntityQuery,
  Role,
  LegalEntityType,
  useTwilioA2PRegistrationStatusQuery,
} from 'shared/types/graphql';
import { formatPhoneNumber } from 'shared/utils';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import AlertBanner from 'shared/components/AlertBanner';
import { BannerType } from 'shared/components/AlertBanner/enums/BannerType';
import ConfigurePhoneNumbersModal from './ConfigurePhoneNumbersModal';
import { RouteDefinitions, SettingsRoutes } from 'routes';
import { useAuth } from 'shared/providers/AuthProvider';
import { useCustomToast } from 'shared/hooks/useCustomToast';
import { ITwilioIncomingPhoneNumber } from 'shared/types/twilio';
import AutoResponseTextArea, { SaveOOOResponse } from './AutoResponseTextArea';
import { A2PRegistrationStatusSection } from './A2PRegistrationStatusSection';
import {
  Button,
  Icon,
  Switch,
  Text,
  Box,
  UnorderedList,
  ListItem,
  Alert,
  Tag,
  Spinner,
  useToast,
  VStack,
  useDisclosure,
} from '@televet/kibble-ui';
import EnableBandwidthPhoneNumber from 'pages/Settings/pages/TextMessagingSettings/EnableBandwidthPhoneNumber';
import EditClinicPhoneNumberButton from 'pages/Settings/pages/TextMessagingSettings/EditClinicPhoneNumberButton';
import AddBandwidthClinicPhoneNumberButton from 'pages/Settings/pages/TextMessagingSettings/AddBandwidthClinicPhoneNumberButton';
import AddBandwidthNumber from 'pages/Settings/pages/TextMessagingSettings/AddBandwidthNumber';
import InfoPopover from 'shared/components/InfoPopover';
import { Status } from '@televet/kibble-ui/build/shared/types';
import BlockedPhoneNumbers from './BlockedPhoneNumbers/BlockedPhoneNumbers';
import { FeatureFlagName } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import AddTwilioClinicPhoneNumberButton from './AddTwilioClinicPhoneNumberButton';
import EnableTwilioPhoneNumber from './EnableTwilioPhoneNumber';
import { PurchaseTwilioNumber } from './PurchaseTwilioNumber';

enum TextMessagingUpdateField {
  InOfficeResponse = 'InOfficeResponse',
  OOOResponse = 'OOOResponse',
}

const TextMessagingSettings = forwardRef(
  (
    {
      hasPermission,
      setHasUnsavedChange,
    }: {
      hasPermission?: boolean;
      setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ): JSX.Element => {
    const history = useHistory();
    const { clinicUser, currentClinicId, currentClinic, isOttoTeamMember } = useClinicUser();
    const { isImpersonating } = useAuth();
    const toast = useToast();
    const { toastMessagePop } = useCustomToast();
    const { isFeatureEnabled } = useFeatureFlag();

    const { isOpen: isConfigurePhoneNumbersModalOpen, onClose: onConfigurePhoneNumbersModalClose } = useDisclosure();

    const useBandwidthForHostingSMS = useMemo(
      () => isFeatureEnabled(FeatureFlagName.UseBandwidthHostingForSMS),
      [isFeatureEnabled],
    );

    const isAdmin = useMemo(() => clinicUser?.vetInfo?.roles?.some((r) => r.role === Role.Admin), [clinicUser]);

    const [phoneNumbersError, setPhoneNumbersError] = useState('');

    const { data, refetch: refetchClinicSmsSettings } = useGetClinicSettingsConversationsSmsQuery({
      variables: {
        id: currentClinicId,
        clinicId: currentClinicId || '',
      },
      skip: !currentClinicId,
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });
    const inOfficeResponseRef = useRef<SaveOOOResponse>(null);
    const oooResponseRef = useRef<SaveOOOResponse>(null);

    const [textMessageChanges, setTextMessagingChanges] = useState<TextMessagingUpdateField[]>([]);

    useEffect(() => {
      setPhoneNumbersError('');
    }, [currentClinicId]);

    const {
      data: phoneNumbersData,
      loading: isClinicPhoneNumbersLoading,
      previousData: previousPhoneNumbersData,
      refetch: refetchPhoneNumbers,
    } = useGetClinicPhoneNumbersQuery({
      variables: {
        where: {
          clinicId: { equals: currentClinicId },
          isDeleted: { equals: false },
          capability: { equals: ServiceCapability.Sms },
        },
      },
      skip: !currentClinicId,
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

    useSubscribeToClinicPhoneNumbersSubscription({
      variables: { where: { clinicId: { equals: currentClinicId } } },
      skip: !currentClinicId,
      shouldResubscribe: true,
    });

    const { data: twilioA2PRegistrationStatus } = useTwilioA2PRegistrationStatusQuery({
      variables: {
        clinicId: currentClinicId ?? '',
      },
      fetchPolicy: GraphQLFetchPolicies.CacheFirst,
      skip: !currentClinicId,
    });

    const messagingService = twilioA2PRegistrationStatus?.twilioA2PRegistrationStatus?.payload.messagingService;
    const brand = twilioA2PRegistrationStatus?.twilioA2PRegistrationStatus?.payload.brand;
    const campaign = twilioA2PRegistrationStatus?.twilioA2PRegistrationStatus?.payload.campaign;

    const [updateClinicSmsSettings, { loading: isUpdatingClinicSmsSettings }] =
      useUpdateClinicSettingsConversationsSmsMutation();
    const clinicPhoneNumbers = phoneNumbersData?.findManyClinicPhoneNumber;
    const smsAutoResponseWhenOpen = data?.findUniqueClinic?.clinicSetting?.smsAutoResponseWhenOpen;
    const smsAutoResponseWhenClosed = data?.findUniqueClinic?.clinicSetting?.smsAutoResponseWhenClosed;
    const hasSmsConversations = !!data?.findUniqueClinic?.hasSmsConversations;

    const { data: legalEntityData } = useFindClinicLegalEntityQuery({
      variables: { where: { id: currentClinicId || '' } },
      fetchPolicy: GraphQLFetchPolicies.CacheFirst,
      skip: !isAdmin || !currentClinicId,
    });

    const doesClinicHaveEIN = useMemo(() => {
      return legalEntityData?.findUniqueClinic?.legalEntity?.ein ? true : false;
    }, [legalEntityData?.findUniqueClinic?.legalEntity?.ein]);

    const isClinicSoleProp = useMemo(() => {
      return legalEntityData?.findUniqueClinic?.legalEntity?.type === LegalEntityType.SoleProprietorship ? true : false;
    }, [legalEntityData?.findUniqueClinic?.legalEntity?.type]);

    const addressLine1 = currentClinic?.addressLine1?.trim();
    const city = currentClinic?.city?.trim();
    const state = currentClinic?.state?.trim();
    const postalCode = currentClinic?.postalCode?.trim();
    const phone = currentClinic?.phone?.trim();

    const hostedPhoneNumber = clinicPhoneNumbers?.some(
      (phoneNumber) =>
        phoneNumber.isPrimary &&
        phoneNumber.isActive &&
        !phoneNumber.isDeleted &&
        phoneNumber.status === PhoneNumberStatus.Hosted,
    );

    const purchasedPhoneNumber = clinicPhoneNumbers?.some(
      (phoneNumber) =>
        phoneNumber.isPrimary &&
        phoneNumber.isActive &&
        !phoneNumber.isDeleted &&
        (phoneNumber.status === PhoneNumberStatus.PurchasedFromTwilio ||
          phoneNumber.status === PhoneNumberStatus.PurchasedFromBandwidth),
    );

    const isSmsToggleDisabled = isUpdatingClinicSmsSettings || (!hostedPhoneNumber && !purchasedPhoneNumber);

    const isHostedWAddress = !!addressLine1 && !!city && !!state && !!postalCode && !!phone;
    const isClinicDataComplete = isHostedWAddress && !!doesClinicHaveEIN;
    const canBeTwilioRegistered =
      !!addressLine1 && !!city && !!state && !!postalCode && (!!isClinicSoleProp || !!doesClinicHaveEIN);

    const handleSmsEnabledToggle = (): void => {
      updateClinicSmsSettings({
        variables: {
          where: { id: currentClinicId },
          data: { hasSmsConversations: !hasSmsConversations },
        },
      });
      toastMessagePop();
    };

    const handleAddPhoneNumberSuccess = (): void => {
      refetchClinicSmsSettings();
      refetchPhoneNumbers();
    };

    const canPurchaseTwilioNumber = useMemo(() => {
      return (
        !useBandwidthForHostingSMS &&
        currentClinic?.twilioConfiguration &&
        brand?.status === 'APPROVED' &&
        campaign?.status === 'APPROVED'
      );
    }, [useBandwidthForHostingSMS, currentClinic?.twilioConfiguration, brand?.status, campaign?.status]);

    const [updateOneClinicPhoneNumber, { loading: isUpdatingClinicPhoneNumber }] =
      useUpdateOneClinicPhoneNumberMutation();

    const [transferTwilioNumber, { loading: isTransferLoading }] =
      useUpdateClinicSettingsConversationsTransferTwilioNumberMutation();

    const handleDeletePhoneNumberClick = async (phoneNumber: string, provider: ServiceProvider): Promise<void> => {
      const phoneNumberSid =
        data?.getTwilioNumbers?.payload?.find(
          (twilioNumber: ITwilioIncomingPhoneNumber) => twilioNumber.phoneNumber === phoneNumber,
        )?.sid || undefined;
      try {
        if (phoneNumberSid) {
          const subaccountSid = data?.findUniqueClinic?.twilioConfiguration?.subaccountSid;
          await transferTwilioNumber({ variables: { phoneNumberSid, fromSubaccountSid: subaccountSid } });
        }

        await updateOneClinicPhoneNumber({
          variables: {
            where: {
              capabilityAndNumberAndProvider: { capability: ServiceCapability.Sms, number: phoneNumber, provider },
            },

            data: { isDeleted: true },
          },
        });
        toast({
          status: 'success',
          title: `Success! ${phoneNumber} was removed from the clinic`,
        });
      } catch (error) {
        Sentry.captureException(error);
        toast({
          status: 'error',
          title: `Whoops! An error occurred while moving ${phoneNumber} from the clinic. Please try again.`,
        });
        console.error(error);
      } finally {
        refetchClinicSmsSettings();
        refetchPhoneNumbers();
      }
    };

    const onResponseChange = useCallback(
      (hasResponseChanged: boolean, field: TextMessagingUpdateField): void => {
        if (hasResponseChanged) {
          setHasUnsavedChange(true);
          setTextMessagingChanges((prevVal) => {
            const newVal = prevVal || [];
            if (newVal.includes(TextMessagingUpdateField[field]) === false) {
              return [...newVal, TextMessagingUpdateField[field]];
            }
            return newVal;
          });
        } else {
          const newState = textMessageChanges?.filter(
            (clinicHourUpdateField) => clinicHourUpdateField !== TextMessagingUpdateField[field],
          );
          setTextMessagingChanges(() => {
            return newState || [];
          });
          if (newState.length === 0) {
            setHasUnsavedChange(false);
          }
        }
      },
      [textMessageChanges, setHasUnsavedChange],
    );

    const onSaveTextMessagingResponseChange = (): void => {
      if (textMessageChanges?.includes(TextMessagingUpdateField.OOOResponse)) {
        oooResponseRef.current?.onSaveOOOResponse();
      }
      if (textMessageChanges?.includes(TextMessagingUpdateField.InOfficeResponse)) {
        inOfficeResponseRef.current?.onSaveOOOResponse();
      }
      setTextMessagingChanges([]);
      refetchClinicSmsSettings();
    };

    const onChangesSaveCancelled = (): void => {
      inOfficeResponseRef.current?.onSetInitialData();
      oooResponseRef.current?.onSetInitialData();
      setTextMessagingChanges([]);
    };

    useImperativeHandle(ref, () => ({
      onLeavingCurrentPage(): void {
        setTextMessagingChanges([]);
      },
      onClickedSaveButton(): void {
        onSaveTextMessagingResponseChange();
      },
      onClickedCancelButton(): void {
        onChangesSaveCancelled();
      },
    }));

    useEffect(() => {
      setHasUnsavedChange(false);
      setTextMessagingChanges([]);
    }, [setHasUnsavedChange]);

    const getTagColors = (status: PhoneNumberStatus): Status => {
      switch (status) {
        case PhoneNumberStatus.PortingInProgress:
          return 'warning';
        case PhoneNumberStatus.HostingNotAvailable:
          return 'error';
        case PhoneNumberStatus.ProblemHosting:
          return 'error';
      }
      return 'info';
    };

    const onAddNumberError = (message?: string): void => {
      setPhoneNumbersError(
        message ?? !isClinicDataComplete
          ? 'Clinic information should be completed before adding a number'
          : 'Unable to add more than two numbers. Please remove a number before adding another.',
      );
    };

    return (
      <SmsSettingsContainer>
        {(isOttoTeamMember || isImpersonating) && (
          <>
            {isConfigurePhoneNumbersModalOpen && (
              <ConfigurePhoneNumbersModal
                onSuccess={handleAddPhoneNumberSuccess}
                onClose={onConfigurePhoneNumbersModalClose}
              />
            )}
            <Header>
              <Title>Text Messaging</Title>
            </Header>
            {!isClinicPhoneNumbersLoading && !clinicPhoneNumbers?.length && (
              <StyledAlertBanner type={BannerType.Info}>
                To enable inbound text messaging for this clinic, please complete the setup process by hosting or
                purchasing a phone number in Twilio and then adding it below.
              </StyledAlertBanner>
            )}
            <SwitchContainer>
              {hasPermission ? (
                <InfoPopover
                  isPopoverDisabled={isSmsToggleDisabled}
                  Trigger={
                    <Switch
                      name="hasSmsConversations"
                      label="Enable inbound text messaging"
                      isChecked={hasSmsConversations}
                      defaultChecked={false}
                      isDisabled={isSmsToggleDisabled}
                      onChange={handleSmsEnabledToggle}
                      isFieldInline
                      formControlStyle={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexDirection: 'row-reverse',
                        width: 'fit-content',
                      }}
                      labelStyle={{
                        marginBottom: '0px',
                        marginLeft: '12px',
                        cursor: isSmsToggleDisabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                  }
                  popoverText="You must host your phone number before you can enable inbound messaging."
                />
              ) : (
                <Text size="md">{hasSmsConversations ? 'Inbound text enabled' : 'Inbound text disabled'}</Text>
              )}
            </SwitchContainer>
          </>
        )}
        {(isOttoTeamMember || isImpersonating) && (
          <SectionContainer>
            <SectionHeader>
              <Subtitle>Twilio A2P Registration</Subtitle>

              <A2PRegistrationStatusSection
                brand={brand}
                campaign={campaign}
                messagingService={messagingService}
                canBeTwilioRegistered={canBeTwilioRegistered}
                isClinicSoleProp={isClinicSoleProp}
              />
            </SectionHeader>
          </SectionContainer>
        )}
        <SectionContainer>
          <SectionHeader>
            <Subtitle>
              <div>Phone Numbers</div>
            </Subtitle>
          </SectionHeader>
          <PhoneNumberList>
            {!previousPhoneNumbersData && isClinicPhoneNumbersLoading ? (
              <Spinner />
            ) : !clinicPhoneNumbers?.length ? (
              <div>This clinic currently has no phone number</div>
            ) : (
              clinicPhoneNumbers?.map(
                ({
                  id,
                  number,
                  status,
                  provider,
                  isPrimary,
                  loaSignedAt,
                  loaSignedByUserId,
                  loaUrl,
                  incomingCarrier,
                  isVerified,
                  portingOrderId,
                }) => {
                  const formattedPhoneNumber = formatPhoneNumber(number);
                  return (
                    <PhoneNumberListItem key={id}>
                      <PhoneNumber>
                        <Number>{formattedPhoneNumber}</Number>
                        <Tag variant={getTagColors(status)} label={`${status.replace(/([A-Z])/g, ' $1').trim()}`} />
                        {isPrimary && (
                          <>
                            &nbsp;
                            <Tag variant="success" label="Active" />
                          </>
                        )}
                        {!isPrimary && status !== PhoneNumberStatus.Hosted && provider === ServiceProvider.Bandwidth && (
                          <>
                            &nbsp;
                            <Tag
                              variant="warning"
                              label="72h Port Time"
                              leftElement={<Icon name="clock" size="sm" />}
                            />
                          </>
                        )}

                        {(isOttoTeamMember || isImpersonating) && (
                          <>
                            &nbsp;
                            <Tag label={provider} />
                          </>
                        )}
                      </PhoneNumber>
                      {useBandwidthForHostingSMS ? (
                        <EnableBandwidthPhoneNumber
                          phoneNumber={formattedPhoneNumber}
                          isPrimary={isPrimary}
                          incomingCarrier={incomingCarrier || 'Unknown'}
                          clinicPhoneNumberId={id}
                          loaSignedAt={loaSignedAt}
                          loaSignedByUser={loaSignedByUserId || undefined}
                          loaUrl={loaUrl}
                          status={status}
                          provider={provider}
                          refetchPhoneNumbers={refetchPhoneNumbers}
                          isClinicDataComplete={isClinicDataComplete}
                        />
                      ) : (
                        <EnableTwilioPhoneNumber
                          phoneNumber={formattedPhoneNumber}
                          isPrimary={isPrimary}
                          incomingCarrier={incomingCarrier || 'Unknown'}
                          clinicPhoneNumberId={id}
                          loaSignedAt={loaSignedAt}
                          loaSignedByUser={loaSignedByUserId || undefined}
                          loaUrl={loaUrl}
                          status={status}
                          provider={provider}
                          refetchPhoneNumbers={refetchPhoneNumbers}
                          isClinicDataComplete={isClinicDataComplete}
                          portingOrderId={portingOrderId}
                        />
                      )}

                      {(isOttoTeamMember || isImpersonating) && (
                        <>
                          <Button
                            size="sm"
                            variant="ghostNeutral"
                            aria-label="Delete phone number"
                            iconName="trashcan"
                            isLoading={isTransferLoading || isUpdatingClinicPhoneNumber}
                            disabled={isTransferLoading || isUpdatingClinicPhoneNumber}
                            onClick={(): void => {
                              if (!isPrimary) {
                                handleDeletePhoneNumberClick(number, provider);
                              } else {
                                setPhoneNumbersError(
                                  'Unable to delete an active number. Please activate another number before deleting this number.',
                                );
                              }
                            }}
                          />
                          <EditClinicPhoneNumberButton
                            currentNumber={number}
                            isPrimary={isPrimary}
                            isVerified={isVerified}
                            clinicPhoneNumberId={id}
                            setPhoneNumbersError={setPhoneNumbersError}
                          />
                        </>
                      )}
                    </PhoneNumberListItem>
                  );
                },
              )
            )}
            <Box margin={'12px 0'} color="text.danger">
              {phoneNumbersError}
            </Box>

            {clinicPhoneNumbers
              ?.filter((clinicPhoneNumber) => clinicPhoneNumber.status === PhoneNumberStatus.ProblemHosting)
              .map((clinicPhoneNumber, index) => {
                return (
                  <Alert
                    key={`problem-hosting-alert-${index}`}
                    status="error"
                    maxWidth={'640px'}
                    marginBottom={'12px'}
                    title={`There was an issue hosting ${formatPhoneNumber(clinicPhoneNumber.number)}`}
                    description="A support ticket has been created and we're working to resolve the issue."
                    hideCloseButton
                  />
                );
              })}
            {clinicPhoneNumbers
              ?.filter((clinicPhoneNumber) => clinicPhoneNumber.status === PhoneNumberStatus.HostingNotAvailable)
              .map((clinicPhoneNumber, index) => {
                return (
                  <Alert
                    key={`problem-hosting-alert-${index}`}
                    status="error"
                    maxWidth={'640px'}
                    marginBottom={'12px'}
                    title={`Hosting not available for ${formatPhoneNumber(clinicPhoneNumber.number)}`}
                    description=" Hosted Messaging cannot be enabled on this number."
                    hideCloseButton
                  />
                );
              })}
            {!isClinicDataComplete && isAdmin && (
              <Alert
                maxWidth={'640px'}
                status="error"
                marginBottom={'12px'}
                hideCloseButton
                title="Incomplete information"
                description={
                  <Box>
                    {isHostedWAddress && !doesClinicHaveEIN
                      ? `We need more information to avoid messages being flagged as spam`
                      : `We need more information before your number can be hosted`}
                    . Please go to{' '}
                    <span
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={(): void => {
                        history.push('/settings/clinic/general');
                      }}
                    >
                      General Info
                    </span>{' '}
                    and update the following fields:
                    <UnorderedList padding={'0 12px'}>
                      {!phone && <ListItem>Clinic Phone Number</ListItem>}
                      {!addressLine1 && <ListItem>Street Address Line 1</ListItem>}
                      {!city && <ListItem>City</ListItem>}
                      {!state && <ListItem>State</ListItem>}
                      {!postalCode && <ListItem>Postal Code</ListItem>}
                      {!doesClinicHaveEIN && <ListItem>Employer Identification Number</ListItem>}
                    </UnorderedList>
                  </Box>
                }
              />
            )}
            {(isOttoTeamMember || isImpersonating) && (
              <VStack alignItems="start">
                {useBandwidthForHostingSMS ? (
                  <AddBandwidthClinicPhoneNumberButton
                    refetchPhoneNumbers={refetchPhoneNumbers}
                    totalNumbers={clinicPhoneNumbers?.length || 0}
                    onAddNumberError={onAddNumberError}
                    isClinicDataComplete={isClinicDataComplete}
                  />
                ) : (
                  <AddTwilioClinicPhoneNumberButton
                    refetchPhoneNumbers={refetchPhoneNumbers}
                    totalNumbers={clinicPhoneNumbers?.length || 0}
                    onAddNumberError={onAddNumberError}
                    isClinicDataComplete={isClinicDataComplete}
                  />
                )}
                {canPurchaseTwilioNumber ? (
                  <PurchaseTwilioNumber
                    totalNumbers={clinicPhoneNumbers?.filter((num) => num.isActive && !num.isDeleted).length || 0}
                    refetchPhoneNumbers={refetchPhoneNumbers}
                  />
                ) : null}
                {useBandwidthForHostingSMS && (
                  <AddBandwidthNumber
                    refetchPhoneNumbers={refetchPhoneNumbers}
                    totalNumbers={clinicPhoneNumbers?.length || 0}
                    onAddNumberError={onAddNumberError}
                    isClinicDataComplete={isClinicDataComplete}
                  />
                )}
              </VStack>
            )}
          </PhoneNumberList>
        </SectionContainer>

        <SectionContainer>
          <SectionHeader>
            <Subtitle>Auto Responses</Subtitle>
            <div>
              Customize automated replies to text messages from clients based on{' '}
              <StyledLink to={`${RouteDefinitions.Settings}${SettingsRoutes.ClinicHours}`}>
                your clinic&apos;s office hours
              </StyledLink>
              .
            </div>
          </SectionHeader>
          <AutoResponseTextArea
            disabled={!hasPermission}
            name="smsAutoResponseWhenOpen"
            label="In-Office Response"
            description="This message is sent at the beginning of new text message conversations while your clinic is open. To disable this response, simply delete the text and save the changes."
            required={false}
            persistedValue={smsAutoResponseWhenOpen}
            isLoading={isClinicPhoneNumbersLoading}
            ref={inOfficeResponseRef}
            onOOOResponseChange={(OOOResponseChanged: boolean): void =>
              onResponseChange(OOOResponseChanged, TextMessagingUpdateField.InOfficeResponse)
            }
          />
          <AutoResponseTextArea
            disabled={!hasPermission}
            name="smsAutoResponseWhenClosed"
            label="Out-Of-Office Response"
            description="This message is sent in response to every incoming text message while your clinic is closed. To disable this response, simply delete the text and save the changes."
            required={false}
            persistedValue={smsAutoResponseWhenClosed}
            isLoading={isClinicPhoneNumbersLoading}
            ref={oooResponseRef}
            onOOOResponseChange={(OOOResponseChanged: boolean): void =>
              onResponseChange(OOOResponseChanged, TextMessagingUpdateField.OOOResponse)
            }
          />
          <p>
            <b>Note: </b>
            {
              "Wildcards are not currently supported. The clinic's {{next open day}} and {{time}} tags will only be used in the default response."
            }
          </p>
        </SectionContainer>
        <BlockedPhoneNumbers />
      </SmsSettingsContainer>
    );
  },
);

TextMessagingSettings.displayName = 'TextMessagingSettings';

const SmsSettingsContainer = styled.div`
  b {
    font-weight: 600;
  }

  p {
    font-size: 14px;
    margin: 10px 0 0;
    max-width: 500px;
    line-height: 1.5;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const SectionContainer = styled.div`
  margin: 30px 0;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

const Subtitle = styled.div`
  width: 100%;
  font-size: 16px;
  font-weight: 600;

  margin: 15px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PhoneNumberList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const PhoneNumberListItem = styled.div`
  display: flex;
  align-items: center;

  & + & {
    margin-top: 15px;
  }

  > * {
    margin-right: 5px;
  }

  > *:first-child {
    margin-right: 15px;
  }
`;

const PhoneNumber = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
`;

const StyledAlertBanner = styled(AlertBanner)`
  margin: 20px 0;
`;

const SwitchContainer = styled.div`
  position: relative;
  z-index: 0;
`;

const StyledLink = styled(Link)`
  color: var(--chakra-colors-text-interactive);
  cursor: pointer;
  text-decoration: underline;
`;

const Number = styled.div`
  margin-right: 10px;
`;

export default TextMessagingSettings;
